// Purdue University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Purdue's Common Data Set 2025-2026 (CDS-2025-2026.xlsx), the file
//    Institutional Data Analytics + Assessment links from its Common Data Set
//    page: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 early plans, and
//    G1/G5 2026-2027 costs from the West Lafayette sheet ("CDS-G - PWL"; the
//    file also has Indianapolis and Polytechnic Statewide cost sheets). The CDS
//    reports Purdue as one institution; its first-year counts are not split by
//    location. C22 prints the early action notification date as 2025-01-15;
//    the page uses the admissions office's January 15 instead.
//  - admissions.purdue.edu: First-Year Application Deadlines (the page carries
//    no cycle year; it is the page as posted for the application that opened
//    August 1, 2026), Your Guide to Applying (testing, location and major,
//    Honors College), First-Year Admission Criteria (course expectations by
//    program), and the Class Profile (admit rate by college for 2025
//    applicants). Purdue "expects" SAT, ACT or CLT scores and accepts an
//    application without one only in exceptional cases; the CDS marks the
//    SAT or ACT as required, so the page says Test-required.
//  - College Scorecard, school id 243780 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// The Class Profile counts 86,953 applications for 2025; the CDS counts
// 87,220. The page uses the CDS counts and shows the profile's rates by
// college with that difference named.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Purdue Common Data Set 2025-2026',
        url: 'https://www.purdue.edu/idata/products-services/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Purdue Admissions, First-Year Application Deadlines',
        url: 'https://admissions.purdue.edu/deadlines/first-year-college-student/',
        read: '2026-10-01',
    },
    guide: {
        label: 'Purdue Admissions, Your Guide to Applying',
        url: 'https://admissions.purdue.edu/become-student/guide/',
        read: '2026-10-01',
    },
    criteria: {
        label: 'Purdue Admissions, First-Year Admission Criteria',
        url: 'https://admissions.purdue.edu/become-student/first-year-criteria/',
        read: '2026-10-01',
    },
    classProfile: {
        label: 'Purdue Admissions, Class Profile',
        url: 'https://admissions.purdue.edu/become-student/class-profile/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?243780-Purdue-University-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'purdue-university',
    kbId: 'purdue_university',
    name: 'Purdue University',
    shortName: 'Purdue',
    location: 'West Lafayette, IN',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Purdue Marketing and Communications,
    // fonts and colors); decoration only.
    branding: {
        primary: '#CFB991',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/purdue_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 87220,
        admitted: 37881,
        enrolled: 9656,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 12040, admitted: 8559 },
            { group: 'Out-of-state', applied: 58853, admitted: 25650 },
            { group: 'International', applied: 16327, admitted: 3672 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'Purdue\'s Class Profile reports an admit rate by college for its 2025 applicants, across West Lafayette and Indianapolis. You apply to a major, and the college here is the one your major belongs to. The profile counts 86,953 applications, a little different from the Common Data Set\'s 87,220.',
        head: ['College or school', 'Admit rate'],
        rows: [
            { name: 'College of Agriculture', rate: 55.8 },
            { name: 'College of Education', rate: 70.5 },
            { name: 'College of Engineering', rate: 34.7 },
            { name: 'College of Health and Human Sciences', rate: 48.7 },
            { name: 'College of Liberal Arts', rate: 53.9 },
            { name: 'College of Pharmacy', rate: 73.9 },
            { name: 'College of Science', rate: 46 },
            { name: 'College of Veterinary Medicine', rate: 30.2 },
            { name: 'Daniels School of Business', rate: 45.9 },
            { name: 'Exploratory Studies', rate: 68.4 },
            { name: 'Polytechnic Institute', rate: 47.1 },
        ],
        source: 'classProfile',
        where: 'Enrollment by College or School',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Purdue has no early decision plan. Its early action plan is not binding. The Class Profile counts 63,883 early action applicants for 2025, but neither it nor the Common Data Set reports how many were admitted early, so there is no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You choose a primary location (West Lafayette or Indianapolis) and a primary major when you apply, and you may add an alternate. Changing majors or locations is never guaranteed. Some programs ask for more:',
        rows: [
            { school: 'Competitive majors', requirement: 'November 1 is the priority deadline for engineering (first-year engineering in West Lafayette; biomedical, computer, electrical, industrial, mechanical and motorsports engineering in Indianapolis), computer science, professional flight technology, nursing and veterinary technology. After November 1, Purdue accepts applications to these programs only if space allows.', source: 'firstYear' },
            { school: 'Engineering', requirement: 'Chemistry experience is expected, on top of the three years of lab science every applicant should have.', source: 'criteria' },
            { school: 'Nursing, pharmacy and veterinary nursing', requirement: 'Of your three years of lab science, one must be chemistry and one must be biology.', source: 'criteria' },
            { school: 'John Martinson Honors College', requirement: 'You apply on the Purdue application by answering four Honors essay prompts, 200 words or fewer each, and must apply by the November 1 Early Action deadline to be considered.', source: 'guide' },
        ],
        source: 'guide',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'January 15', binding: false },
            { plan: 'Regular decision', deadline: 'January 15', decision: 'By March 31', binding: false },
        ],
        note: 'The application opens August 1 on the Common App, and the fee is $60. November 1 is also the deadline for merit scholarship and Honors College consideration, and Purdue says admission is much more competitive if you apply later.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Purdue expects first-year applicants to have SAT, ACT or CLT scores, with no preference among them, and you self-report scores on the application. Only in exceptional cases, where an applicant could not take a test, does Purdue allow an application without one. For a given test you may combine your highest section scores across dates. Scores must be from within the past five years, and the ACT Science section is not required.',
        source: 'guide',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'guide',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9718, 28520] },
            { item: 'Required fees', amounts: [274, 274] },
            { item: 'Housing (on campus)', amounts: [10662, 10662] },
            { item: 'Food (on-campus meal plan)', amounts: [6072, 6072] },
            { item: 'Books and supplies', amounts: [1090, 1090] },
            { item: 'Transportation', amounts: [570, 570] },
            { item: 'Other expenses', amounts: [2200, 2200] },
        ],
        totals: [30586, 49388],
        note: 'For a full-time undergraduate at West Lafayette living on campus, fall and spring. International students pay $31,830 in tuition, and Purdue reports that tuition and fees vary by program.',
        source: 'cds',
        where: 'sections G1 and G5 (West Lafayette)',
        netPrice: {
            average: 14851,
            byIncome: [
                { income: '$0 to $30,000', amount: 6147 },
                { income: '$30,001 to $48,000', amount: 5618 },
                { income: '$48,001 to $75,000', amount: 9056 },
                { income: '$75,001 to $110,000', amount: 15253 },
                { income: 'Over $110,000', amount: 23490 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Purdue\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
